/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

const {
	//B2C
	REACT_APP_CLIENT_ID,
	REACT_APP_TENANT,
	REACT_APP_AUTHORITY_DOMAIN,
	REACT_APP_SIGN_UP_SIGN_IN_POLICY,
	REACT_APP_SIGN_UP_POLICY,
	REACT_APP_FORGOT_PASSWORD_POLICY,

	//API
	REACT_APP_API_APP_ID_URI,
	REACT_APP_API_SCOPES
} = process.env;

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */

export const b2cPolicies = {
	names: {
		signUpSignIn: REACT_APP_SIGN_UP_SIGN_IN_POLICY,
		signUp: REACT_APP_SIGN_UP_POLICY,
		forgotPassword: REACT_APP_FORGOT_PASSWORD_POLICY
	},
	authorities: {
		signUpSignIn: {
			authority: `https://${REACT_APP_AUTHORITY_DOMAIN}/${REACT_APP_TENANT}/${REACT_APP_SIGN_UP_SIGN_IN_POLICY}`
		},
		signUp: {
			authority: `https://${REACT_APP_AUTHORITY_DOMAIN}/${REACT_APP_TENANT}/${REACT_APP_SIGN_UP_POLICY}`
		},
		forgotPassword: {
			authority: `https://${REACT_APP_AUTHORITY_DOMAIN}/${REACT_APP_TENANT}/${REACT_APP_FORGOT_PASSWORD_POLICY}`
		}
	},
	authorityDomain: REACT_APP_AUTHORITY_DOMAIN
};

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
	auth: {
		clientId: REACT_APP_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
		authority: b2cPolicies.authorities.signUpSignIn.authority, // Use a sign-up/sign-in user-flow as a default authority
		knownAuthorities: [ b2cPolicies.authorityDomain ], // Mark your B2C tenant's domain as trusted.
		redirectUri: '/', // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
		postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
		navigateToLoginRequestUrl: true // If "true", will navigate back to the original request location before processing the auth code response.
	},
	cache: {
		cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
		storeAuthStateInCookie: false // Set this to "true" if you are having issues on IE11 or Edge
	},
	system: {
		loggerOptions: {
			loggerCallback: (level, message, containsPii) => {
				if (containsPii) {
					return;
				}
				switch (level) {
					case LogLevel.Error:
						console.error(message);
						return;
					case LogLevel.Info:
						console.info(message);
						return;
					case LogLevel.Verbose:
						console.debug(message);
						return;
					case LogLevel.Warning:
						console.warn(message);
						return;
				}
			}
		}
	}
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
	scopes: []
};

/**
 * Scopes you add here will be used to request a token from Azure AD B2C to be used for accessing a protected resource.
 * To learn more about how to work with scopes and resources, see: 
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const tokenRequest = () => {
	let scopes = JSON.parse(REACT_APP_API_SCOPES);
	let tokenScopes = scopes.map((scope) => {
		return REACT_APP_API_APP_ID_URI + '/' + scope;
	});

	return {
		scopes: tokenScopes // e.g. ["https://fabrikamb2c.onmicrosoft.com/helloapi/demo.read"]
		// forceRefresh: false // Set this to "true" to skip a cached token and go to the server to get a new token
	};
};
